import { useEffect, useMemo, useRef, useState } from "react";
import { Wallet, CreditCard, QrCode, Landmark, Smartphone, Loader2, Banknote } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { rp, num, parseNum } from "../../lib/format";
import { Switch } from "../ui/switch";
import { CustomerPicker } from "./CustomerPicker";

const METHOD_LIST = [
  ["tunai", "Tunai", Banknote],
  ["debit", "Debit", CreditCard],
  ["kredit", "Kredit", Wallet],
  ["qris", "QRIS", QrCode],
  ["transfer", "Transfer", Landmark],
  ["ewallet", "E-Wallet", Smartphone],
];

const suggestions = (total) => {
  const set = new Set([total]);
  [5000, 10000, 20000, 50000, 100000].forEach((s) => set.add(Math.ceil(total / s) * s));
  [50000, 100000].forEach((v) => v > total && set.add(v));
  return [...set].filter((v) => v >= total).sort((a, b) => a - b).slice(0, 6);
};

export const PaymentDialog = ({ open, onOpenChange, base, taxPercent = 0, store, onPay, loading }) => {
  const [method, setMethod] = useState("tunai");
  const [paid, setPaid] = useState("");
  const [customer, setCustomer] = useState(null);
  const [redeem, setRedeem] = useState(false);
  const [note, setNote] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMethod("tunai");
      setPaid("");
      setCustomer(null);
      setRedeem(false);
      setNote("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const loyalty = !!store?.loyalty_enabled;
  const pv = Number(store?.point_value || 0);
  const redeemPts = loyalty && redeem && customer && pv > 0 ? Math.min(customer.points || 0, Math.floor(base / pv)) : 0;
  const ptsDisc = Math.min(redeemPts * pv, base);
  const after = base - ptsDisc;
  const total = after + Math.round((after * taxPercent) / 100);
  const earn = loyalty && customer ? Math.floor(total / Number(store.points_per_amount || 1)) : 0;
  const paidNum = method === "tunai" ? parseNum(paid) : total;
  const change = paidNum - total;
  const canPay = method !== "tunai" || paidNum >= total;
  const quick = useMemo(() => suggestions(total), [total]);

  const submit = (e) => {
    e?.preventDefault();
    if (!canPay || loading) return;
    onPay({ payment_method: method, paid: paidNum, customer_id: customer?.id || "", customer_name: customer?.name || "", redeem_points: redeemPts, note });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Pembayaran</DialogTitle>
          <DialogDescription>Pilih metode bayar lalu konfirmasi.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label>Pelanggan / Member</Label>
            <CustomerPicker value={customer} onChange={setCustomer} />
            {loyalty && customer && (
              <label className="flex items-center justify-between text-sm rounded-lg bg-muted/60 px-3 py-2">
                <span>Tukar poin {customer.points > 0 ? `(maks ${num(Math.min(customer.points, pv ? Math.floor(base / pv) : 0))} poin = ${rp(Math.min(customer.points * pv, base))})` : "(poin kosong)"}</span>
                <Switch checked={redeem} disabled={!customer.points} onCheckedChange={setRedeem} data-testid="redeem-points-switch" />
              </label>
            )}
          </div>
          <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 text-center">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Tagihan</div>
            <div className="font-mono font-bold text-3xl text-primary" data-testid="payment-total">{rp(total)}</div>
            {ptsDisc > 0 && <div className="text-xs text-success mt-1" data-testid="payment-points-discount">Potongan poin -{rp(ptsDisc)}</div>}
            {earn > 0 && <div className="text-xs text-muted-foreground mt-1" data-testid="payment-points-earn">Member mendapat +{num(earn)} poin</div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {METHOD_LIST.map(([k, l, Icon]) => (
              <button type="button" key={k} onClick={() => setMethod(k)} data-testid={`pay-method-${k}`}
                className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-sm font-medium transition-colors ${
                  method === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"
                }`}>
                <Icon className="h-5 w-5" />
                {l}
              </button>
            ))}
          </div>
          {method === "tunai" ? (
            <div className="space-y-3">
              <Label>Uang Diterima</Label>
              <Input ref={inputRef} inputMode="numeric" className="h-12 text-xl font-mono font-bold" placeholder="0"
                value={paid ? num(parseNum(paid)) : ""} onChange={(e) => setPaid(e.target.value)} data-testid="payment-cash-input" />
              <div className="grid grid-cols-3 gap-2">
                {quick.map((v, i) => (
                  <Button type="button" key={v} variant="outline" className="font-mono text-xs" onClick={() => setPaid(String(v))} data-testid={`quick-cash-${i}`}>
                    {i === 0 ? "Uang Pas" : num(v)}
                  </Button>
                ))}
              </div>
              <div className={`flex justify-between items-center rounded-xl p-4 ${change >= 0 ? "bg-success/10" : "bg-destructive/10"}`}
                style={{ background: change >= 0 ? "hsl(var(--success) / .12)" : undefined }}>
                <span className="font-medium">{change >= 0 ? "Kembalian" : "Kurang"}</span>
                <span className={`font-mono font-bold text-2xl ${change >= 0 ? "text-success" : "text-destructive"}`} data-testid="payment-change">{rp(Math.abs(change))}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground rounded-xl bg-muted p-3" data-testid="non-cash-info">
              Pastikan pembayaran {method.toUpperCase()} sebesar <b className="font-mono">{rp(total)}</b> sudah diterima di mesin EDC / aplikasi sebelum konfirmasi.
            </p>
          )}
          <div className="space-y-1.5">
            <Label>Catatan</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opsional" data-testid="payment-note-input" />
          </div>
          <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={!canPay || loading} data-testid="confirm-payment-btn">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Konfirmasi Bayar (Enter)
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
